import { useEffect, useMemo, useRef, useState } from 'react';
import type { LegacyMilestone } from '../data/legacyMilestones';

type NarrationDurations = {
  bySrc: Record<string, number>;
  yearTotals: Record<string, number>;
  totalSeconds: number;
  yearSources: Record<string, string[]>;
  expectedCount: number;
  loadedCount: number;
};

export const useNarrationDurations = (
  milestones: LegacyMilestone[],
  getIntroAudioSrc: (year: string) => string,
  getPhotoAudioSrc: (year: string, index: number) => string
): NarrationDurations => {
  const yearSources = useMemo(() => {
    const map: Record<string, string[]> = {};
    milestones.forEach((milestone) => {
      const sources: string[] = [];
      sources.push(getIntroAudioSrc(milestone.year));
      const count = (milestone.photos ?? []).length;
      for (let i = 0; i < count; i += 1) {
        sources.push(getPhotoAudioSrc(milestone.year, i));
      }
      map[milestone.year] = sources;
    });
    return map;
  }, [milestones, getIntroAudioSrc, getPhotoAudioSrc]);

  const expectedCount = useMemo(() => {
    const sources = Object.values(yearSources).flat();
    return new Set(sources).size;
  }, [yearSources]);

  const [bySrc, setBySrc] = useState<Record<string, number>>({});
  const audioContextRef = useRef<AudioContext | null>(null);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    let cancelled = false;
    setBySrc({});
    const sources = Object.values(yearSources).flat();
    const uniqueSources = Array.from(new Set(sources));

    const cacheKey = 'narration-duration-cache-v1';
    let cached: Record<string, number> = {};
    const cachedRaw = window.localStorage.getItem(cacheKey);
    if (cachedRaw) {
      try {
        cached = JSON.parse(cachedRaw) as Record<string, number>;
      } catch {
        cached = {};
      }
    }
    if (cached && typeof cached === 'object') {
      const seeded: Record<string, number> = {};
      uniqueSources.forEach((src) => {
        if (Number.isFinite(cached[src])) {
          seeded[src] = cached[src];
        }
      });
      if (Object.keys(seeded).length > 0) {
        setBySrc(seeded);
      }
      if (Object.keys(seeded).length === uniqueSources.length) {
        return () => {
          cancelled = true;
        };
      }
    }

    const ensureAudioContext = () => {
      if (audioContextRef.current) return audioContextRef.current;
      const AudioContextCtor =
        window.AudioContext ?? (window as typeof window & { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
      if (!AudioContextCtor) return null;
      audioContextRef.current = new AudioContextCtor();
      return audioContextRef.current;
    };

    const loadDurationFromMetadata = (srcWithBust: string) =>
      new Promise<number>((resolve) => {
        const audio = new Audio();
        const timeoutId = window.setTimeout(() => {
          cleanup(0);
        }, 4000);
        const cleanup = (duration: number) => {
          window.clearTimeout(timeoutId);
          audio.onloadedmetadata = null;
          audio.onerror = null;
          resolve(duration);
        };
        audio.preload = 'metadata';
        audio.onloadedmetadata = () => {
          const duration = Number.isFinite(audio.duration) ? audio.duration : 0;
          cleanup(duration);
        };
        audio.onerror = () => {
          cleanup(0);
        };
        audio.src = srcWithBust;
        audio.load();
      });

    const loadDurationForSrc = async (src: string) => {
      const cacheBuster = `t=${Date.now()}`;
      const srcWithBust = src.includes('?') ? `${src}&${cacheBuster}` : `${src}?${cacheBuster}`;
      try {
        const controller = new AbortController();
        const timeoutId = window.setTimeout(() => controller.abort(), 12000);
        const response = await fetch(srcWithBust, { signal: controller.signal });
        const arrayBuffer = await response.arrayBuffer();
        window.clearTimeout(timeoutId);
        const context = ensureAudioContext();
        if (context) {
          const buffer = await context.decodeAudioData(arrayBuffer.slice(0));
          const duration = Number.isFinite(buffer.duration) ? buffer.duration : 0;
          if (duration > 0) {
            return duration;
          }
        }
      } catch {
        // fall through to metadata loader
      }
      return loadDurationFromMetadata(srcWithBust);
    };

    const maxConcurrent = 3;
    let cursor = 0;

    const runWorker = async () => {
      while (!cancelled) {
        const index = cursor;
        if (index >= uniqueSources.length) return;
        cursor += 1;
        const src = uniqueSources[index];
        if (cached && Number.isFinite(cached[src])) {
          continue;
        }
        const duration = await loadDurationForSrc(src);
        if (cancelled) return;
        setBySrc((prev) => {
          if (prev[src] !== undefined) return prev;
          const next = { ...prev, [src]: duration };
          window.localStorage.setItem(cacheKey, JSON.stringify(next));
          return next;
        });
      }
    };

    for (let i = 0; i < maxConcurrent; i += 1) {
      void runWorker();
    }
    return () => {
      cancelled = true;
      if (audioContextRef.current) {
        audioContextRef.current.close().catch(() => {});
        audioContextRef.current = null;
      }
    };
  }, [yearSources]);

  const { yearTotals, totalSeconds } = useMemo(() => {
    const totals: Record<string, number> = {};
    let total = 0;
    Object.entries(yearSources).forEach(([year, sources]) => {
      const sum = sources.reduce((acc, src) => acc + (bySrc[src] ?? 0), 0);
      totals[year] = sum;
      total += sum;
    });
    return { yearTotals: totals, totalSeconds: total };
  }, [bySrc, yearSources]);

  const loadedCount = Object.keys(bySrc).length;

  return { bySrc, yearTotals, totalSeconds, yearSources, expectedCount, loadedCount };
};
